/**
 * student/campaigns — HOOKS.
 * The React-facing layer: query hooks (read) + mutation hooks (write). Mutations
 * own cache invalidation via the domain key factory — components never invalidate
 * directly. Completing an activity / submitting a reflection also refreshes the
 * progress + portfolio domains (XP/KP/badges/skills change server-side).
 */
import { useMutation, useQueries, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { studentCampaignsApi } from './api';
import { studentCampaignsKeys } from './keys';
import { studentProgressKeys } from '../progress/keys';
import { studentPortfolioKeys } from '../portfolio/keys';
import type { StudentCampaignDetail } from './types';
import { nextGameplayState, pickNextGameplay, runsOutside, type NextGameplayState } from './gating';
import { useStudentSessions } from '../sessions/hooks';

// Completing an activity / submitting a reflection changes XP/KP/badges/skills
// server-side, so the progress + portfolio domains are invalidated too.
function invalidateProgressAndPortfolio(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: studentProgressKeys.all() });
  qc.invalidateQueries({ queryKey: studentPortfolioKeys.all() });
}

export function useStudentCampaigns() {
  return useQuery({
    queryKey: studentCampaignsKeys.lists(),
    queryFn: () => studentCampaignsApi.listMine(),
  });
}

/** True while any gameplay match in the campaign is live (session RUNNING). */
function hasLiveGameplay(detail?: StudentCampaignDetail): boolean {
  return !!detail?.chapters.some((ch) =>
    ch.activities.some((a) => a.type === 'GAMEPLAY' && a.worldSession?.status === 'RUNNING'),
  );
}

/** A campaign's detail as a query, its key and its fetch: read alone by the campaign page, and many at once by the home's Up Next. */
const detailQuery = (id: string) => ({ queryKey: studentCampaignsKeys.detail(id), queryFn: () => studentCampaignsApi.get(id) });

export function useStudentCampaign(id: string | null) {
  return useQuery({
    ...detailQuery(id ?? ''),
    enabled: !!id,
    // While a gameplay match is live, poll so the FINISHED + completion state
    // (recorded server-side a few seconds after the match ends) shows up on its
    // own — instead of being stuck on the pre-match state until a manual
    // refresh. Pauses while the tab is hidden; stops once no session is RUNNING.
    refetchInterval: (query) => (hasLiveGameplay(query.state.data) ? 5_000 : false),
    refetchIntervalInBackground: false,
  });
}

interface NextCampaignGameplay {
  campaignId: string;
  campaignName: string;
  /** Chapter that holds the gameplay activity. */
  chapterId: string;
  activityId: string;
  activityTitle: string;
  startTimeUtc: number;
  isRunning: boolean;
  state: NextGameplayState;
  /** # of incomplete required activities before the gameplay (for the warning). */
  pendingCount: number;
  /** Chapter holding the next required activity — where 'pending' clicks go. */
  targetChapterId: string;
  /** Start date of the gameplay's chapter (for the 'date-locked' message). */
  chapterStartDateUtc: number | null;
}

/**
 * The next GAMEPLAY activity session across the student's campaigns that will
 * happen or is happening now — plus whether prerequisite activities block it.
 * Drives the home "Up Next" banner. Aggregates campaign details on the client
 * (reusing the cached `/campaigns/:id` detail), no extra backend: the active
 * campaigns', and the ended campaigns' too only while a session the student's
 * list says is running belongs to none of the active ones (runsOutside) — a
 * session already running across its campaign's end finishes, and stays
 * joinable (pickNextGameplay, gating.ts). The list is read only for a student
 * with an ended campaign, once a mount, unpolled.
 */
export function useNextCampaignGameplay(): NextCampaignGameplay | null {
  const summaries = useStudentCampaigns().data ?? [];
  const endedIds = summaries.filter((c) => c.status === 'FINISHED').map((c) => c.id);
  const sessions = useStudentSessions({ enabled: endedIds.length > 0, poll: false }).data;

  const active = useQueries({ queries: summaries.filter((c) => c.status === 'ACTIVE').map((c) => detailQuery(c.id)) });
  const activeDetails = active.map((q) => q.data).filter((d): d is StudentCampaignDetail => !!d);
  const runningElsewhere =
    active.every((q) => !q.isPending) && runsOutside(activeDetails, [...(sessions?.upcoming ?? []), ...(sessions?.past ?? [])]);
  const ended = useQueries({ queries: runningElsewhere ? endedIds.map(detailQuery) : [] });
  const endedDetails = ended.map((q) => q.data).filter((d): d is StudentCampaignDetail => !!d);

  const next = pickNextGameplay([...activeDetails, ...endedDetails]);
  if (!next) return null;
  const { state, pendingCount, targetChapterId } = nextGameplayState(next.campaign, next.chapter, next.activity);

  return {
    campaignId: next.campaign.id,
    campaignName: next.campaign.name,
    chapterId: next.chapter.id,
    activityId: next.activity.id,
    activityTitle: next.activity.title,
    startTimeUtc: next.startTimeUtc,
    isRunning: next.running,
    state,
    pendingCount,
    targetChapterId,
    chapterStartDateUtc: next.chapter.startDateUtc,
  };
}

export function useStudentCampaignArticle(campaignId: string, activityId: string) {
  return useQuery({
    queryKey: studentCampaignsKeys.article(campaignId, activityId),
    queryFn: () => studentCampaignsApi.getArticle(campaignId, activityId),
  });
}

export function useStudentMaterialArticle(campaignId: string, materialId: string, enabled = true) {
  return useQuery({
    queryKey: studentCampaignsKeys.materialArticle(campaignId, materialId),
    queryFn: () => studentCampaignsApi.getMaterialArticle(campaignId, materialId),
    enabled,
  });
}

export function useGameplayContext(campaignId: string, activityId: string, enabled: boolean) {
  return useQuery({
    queryKey: studentCampaignsKeys.gameplayContext(campaignId, activityId),
    queryFn: () => studentCampaignsApi.getGameplayContext(campaignId, activityId),
    enabled,
  });
}

export function useCompleteActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { campaignId: string; activityId: string; result?: Record<string, unknown> }) =>
      studentCampaignsApi.complete(vars.campaignId, vars.activityId, vars.result),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: studentCampaignsKeys.all() });
      invalidateProgressAndPortfolio(qc);
    },
  });
}

export function useSubmitGameplayReflection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      campaignId: string;
      activityId: string;
      answers: Record<string, string>;
      endorsements: { nakamaId: string; tagId: string }[];
    }) =>
      studentCampaignsApi.submitGameplayReflection(
        vars.campaignId,
        vars.activityId,
        vars.answers,
        vars.endorsements,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: studentCampaignsKeys.all() });
      invalidateProgressAndPortfolio(qc);
    },
  });
}
